// Schema.org descriptions of what a page is about.
//
// The site-wide Organization / WebSite / MobileApplication graph is static and
// lives in public/index.html so it is readable without running any JavaScript.
// What is here describes individual pages, whose subject is only known once the
// route — and sometimes its data — has resolved.
//
// Every block anchors back to the static graph through `@id` references
// (`#organization`, `#website`) rather than restating the publisher, which is
// what lets Google join them into one entity instead of several.

import { Bar } from '../types';
import { SITE_ORIGIN, absoluteUrl } from './seo';

/** Seconds since local midnight -> "HH:MM", wrapping a 3am close back round. */
function clockTime(seconds: number): string {
  const wrapped = ((seconds % 86400) + 86400) % 86400;
  const hours = Math.floor(wrapped / 3600);
  const minutes = Math.floor((wrapped % 3600) / 60);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * Closing time, with midnight written as 23:59.
 *
 * A bar that shuts at midnight stores 86400, which wraps to "00:00" — and
 * "opens 15:00, closes 00:00" is a span schema.org readers cannot make sense
 * of. 23:59 is the convention for exactly midnight; anything genuinely past it
 * (a 3am close, stored as 97200) wraps to "03:00" and is read correctly as
 * running into the next day.
 */
function closingTime(seconds: number): string {
  return seconds > 0 && seconds % 86400 === 0 ? '23:59' : clockTime(seconds);
}

type OpeningHoursSpecification = {
  '@type': 'OpeningHoursSpecification';
  dayOfWeek: string;
  opens: string;
  closes: string;
};

/**
 * Opening hours in schema.org's shape.
 *
 * `-1` is the data's "closed" sentinel and a day that is shut is simply left
 * out — schema.org reads an absent day as closed, and stating "00:00 to 00:00"
 * would instead claim it is open around the clock.
 */
function openingHours(bar: Bar): OpeningHoursSpecification[] {
  return bar.openingTimes
    .filter((time) => time.open >= 0 && time.close >= 0 && time.close !== time.open)
    .map((time) => ({
      '@type': 'OpeningHoursSpecification' as const,
      dayOfWeek: time.dayIndex,
      opens: clockTime(time.open),
      closes: closingTime(time.close),
    }));
}

/** Cheapest and dearest listed price, for `priceRange`. */
function priceRange(bar: Bar, currency: string): string | undefined {
  if (!currency) return undefined;

  const prices = bar.beers
    .flatMap((beer) => beer.sizes.map((size) => size.regularPrice))
    .filter((price) => price > 0);

  if (prices.length === 0) return undefined;

  const low = Math.min(...prices);
  const high = Math.max(...prices);
  const format = (value: number) => `${currency} ${value.toFixed(2)}`;

  return low === high ? format(low) : `${format(low)} - ${format(high)}`;
}

/**
 * One bar, as the venue a shared link is about.
 *
 * Deliberately omits `aggregateRating`: the ratings shown in the panel come
 * from Google Places, and marking up someone else's ratings as this page's own
 * is exactly what Google's structured data guidelines forbid.
 */
export function barStructuredData(bar: Bar, currency: string, pageUrl: string): object {
  const hours = openingHours(bar);
  const range = priceRange(bar, currency);

  return {
    '@context': 'https://schema.org',
    '@type': 'BarOrPub',
    '@id': `${pageUrl}#bar`,
    name: bar.name,
    url: pageUrl,
    address: {
      '@type': 'PostalAddress',
      streetAddress: bar.address,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: bar.lat,
      longitude: bar.lng,
    },
    ...(bar.phone ? { telephone: bar.phone } : null),
    ...(bar.description ? { description: bar.description } : null),
    ...(bar.website ? { sameAs: [bar.website] } : null),
    ...(hours.length > 0 ? { openingHoursSpecification: hours } : null),
    ...(range ? { priceRange: range } : null),
    mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl },
  };
}

/**
 * The trail from the home page to this one.
 *
 * Every page below the root gets one: it is what puts a readable path under a
 * result in search rather than a bare URL, and it is the machine-readable half
 * of the internal linking blueprint in SEO.md — the crawler is told the same
 * hierarchy the navigation shows.
 *
 * `crumbs` excludes the home page, which is always prepended.
 */
export function breadcrumbStructuredData(
  crumbs: { name: string; path: string }[]
): object {
  const trail = [{ name: 'Home', path: '/' }, ...crumbs];

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${absoluteUrl(trail[trail.length - 1].path)}#breadcrumb`,
    itemListElement: trail.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: absoluteUrl(crumb.path),
    })),
  };
}

/**
 * A city's map page: a collection of venues, about that city.
 *
 * `CollectionPage` rather than `WebPage` because that is what it is — one page
 * standing for every bar in a city. The city itself goes in `about`, which is
 * the field that ties the page to a place Google already has an entity for.
 */
export function cityPageStructuredData(city: {
  name: string;
  country: string;
  slug: string;
  title: string;
  description: string;
}): object {
  const url = absoluteUrl(`/${city.slug}`);

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${url}#webpage`,
    url,
    name: city.title,
    description: city.description,
    inLanguage: 'en',
    isPartOf: { '@id': `${SITE_ORIGIN}/#website` },
    publisher: { '@id': `${SITE_ORIGIN}/#organization` },
    about: {
      '@type': 'City',
      name: city.name,
      address: {
        '@type': 'PostalAddress',
        addressLocality: city.name,
        addressCountry: city.country,
      },
    },
    breadcrumb: { '@id': `${url}#breadcrumb` },
  };
}

/**
 * An ordinary content page — the ones that are read rather than used.
 */
export function webPageStructuredData(page: {
  title: string;
  description: string;
  path: string;
}): object {
  const url = absoluteUrl(page.path);

  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${url}#webpage`,
    url,
    name: page.title,
    description: page.description,
    inLanguage: 'en',
    isPartOf: { '@id': `${SITE_ORIGIN}/#website` },
    publisher: { '@id': `${SITE_ORIGIN}/#organization` },
    breadcrumb: { '@id': `${url}#breadcrumb` },
  };
}

/**
 * The FAQ page, marked up for the question-and-answer result type.
 *
 * Built from the same array the page renders, never a second copy: Google
 * requires the marked-up answer to be the answer a visitor actually sees, and
 * two lists would drift apart on the first edit.
 */
export function faqStructuredData(
  faqs: { question: string; answer: string }[],
  path: string
): object {
  const url = absoluteUrl(path);

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${url}#faq`,
    url,
    isPartOf: { '@id': `${SITE_ORIGIN}/#website` },
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}
